import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  Clock3,
  Heart,
  Loader2,
  MapPin,
  RefreshCw,
  Star,
} from "lucide-react";

import ChaafoLogo from "../Images/Item_Image_Placeholder.webp";
import { useNavigate } from "react-router-dom";
import { useRestaurant } from "../Services/useRestaurant";
import { useGlobalContext } from "../Context/GlobalContext";

const RESTAURANT_RANGE_KM = 10;
const AUTO_SCROLL_DELAY = 3000;
const AUTO_SCROLL_RESUME_DELAY = 1800;

const getLocationErrorMessage = (error) => {
  if (!error) {
    return "Unable to access your current location.";
  }

  if (error.code === 1) {
    return "Location permission was denied. Please allow location access from browser settings.";
  }

  if (error.code === 2) {
    return "Your current location is unavailable.";
  }

  if (error.code === 3) {
    return "Location request timed out. Please try again.";
  }

  return error.message || "Unable to access your current location.";
};

const getRestaurantUserId = (restaurant) => {
  return String(
    restaurant?.userId ||
      restaurant?.userid ||
      restaurant?.user_id ||
      restaurant?.restaurantUserId ||
      restaurant?.shopCode ||
      restaurant?.code ||
      restaurant?.raw?.userId ||
      restaurant?.raw?.userid ||
      restaurant?.raw?.user_id ||
      "",
  ).trim();
};

const getValidCoordinate = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") {
      continue;
    }

    const coordinate = Number(value);

    if (Number.isFinite(coordinate)) {
      return coordinate;
    }
  }

  return null;
};

const getStoredValue = (...keys) => {
  try {
    for (const key of keys) {
      const sessionValue = sessionStorage.getItem(key);

      if (sessionValue !== null) {
        return sessionValue;
      }

      const localValue = localStorage.getItem(key);

      if (localValue !== null) {
        return localValue;
      }
    }
  } catch (error) {
    console.error("Unable to read coordinate from storage:", error);
  }

  return null;
};

const getStoredObject = (...keys) => {
  try {
    for (const key of keys) {
      const rawValue = sessionStorage.getItem(key) || localStorage.getItem(key);

      if (!rawValue) {
        continue;
      }

      try {
        const parsedValue = JSON.parse(rawValue);

        if (parsedValue && typeof parsedValue === "object") {
          return parsedValue;
        }
      } catch {
        // String values ignore honge.
      }
    }
  } catch (error) {
    console.error("Unable to read location object:", error);
  }

  return {};
};

const getRestaurantCoordinates = (restaurant) => {
  const rawRestaurant = restaurant?.raw || {};

  const latitude = getValidCoordinate(
    restaurant?.latitude,
    restaurant?.lat,

    rawRestaurant?.latitude,
    rawRestaurant?.lat,
    rawRestaurant?.shop_latitude,
    rawRestaurant?.restaurant_latitude,
    rawRestaurant?.customerLatitude,
  );

  const longitude = getValidCoordinate(
    restaurant?.longitude,
    restaurant?.lontitude,
    restaurant?.lng,
    restaurant?.lon,

    rawRestaurant?.longitude,
    rawRestaurant?.lontitude,
    rawRestaurant?.lng,
    rawRestaurant?.lon,
    rawRestaurant?.shop_longitude,
    rawRestaurant?.restaurant_longitude,
    rawRestaurant?.customerLongitude,
  );

  return {
    latitude,
    longitude,
  };
};

const calculateDistance = (
  customerLatitude,
  customerLongitude,
  restaurantLatitude,
  restaurantLongitude,
) => {
  const customerLat = Number(customerLatitude);

  const customerLng = Number(customerLongitude);

  const restaurantLat = Number(restaurantLatitude);

  const restaurantLng = Number(restaurantLongitude);

  if (
    !Number.isFinite(customerLat) ||
    !Number.isFinite(customerLng) ||
    !Number.isFinite(restaurantLat) ||
    !Number.isFinite(restaurantLng)
  ) {
    return null;
  }

  const earthRadius = 6371;

  const toRadians = (value) => (value * Math.PI) / 180;

  const latitudeDifference = toRadians(restaurantLat - customerLat);

  const longitudeDifference = toRadians(restaurantLng - customerLng);

  const firstLatitude = toRadians(customerLat);

  const secondLatitude = toRadians(restaurantLat);

  const value =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(firstLatitude) *
      Math.cos(secondLatitude) *
      Math.sin(longitudeDifference / 2) ** 2;

  const centralAngle = 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));

  return earthRadius * centralAngle;
};

const ShimmerStyles = () => (
  <style>
    {`
      @keyframes restaurantCardShimmer {
        0% {
          transform: translateX(-120%);
        }

        100% {
          transform: translateX(120%);
        }
      }

      .restaurant-card-shimmer {
        position: relative;
        overflow: hidden;
        background: #e5e7eb;
      }

      .restaurant-card-shimmer::after {
        content: "";
        position: absolute;
        inset: 0;

        transform: translateX(-120%);

        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.35) 30%,
          rgba(255, 255, 255, 0.95) 50%,
          rgba(255, 255, 255, 0.35) 70%,
          transparent 100%
        );

        animation: restaurantCardShimmer 1.25s linear infinite;
        will-change: transform;
        pointer-events: none;
      }
/* ===================================================== */
/* NEAR YOU - VERTICAL SLIDE ANIMATION */
/* ===================================================== */

@keyframes nearYouDetailsSlide {
  /* KM + TIME initially visible */
  0%,
  34% {
    transform: translateY(0);
    opacity: 1;
  }

  /* KM + TIME goes upward */
  42% {
    transform: translateY(-120%);
    opacity: 0;
  }

  /* Keep it above while Near From You is visible */
  43%,
  78% {
    transform: translateY(-120%);
    opacity: 0;
  }

  /* Prepare above for downward return */
  79% {
    transform: translateY(-120%);
    opacity: 0;
  }

  /* Comes downward into original position */
  88%,
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes nearYouLabelSlide {
  /* Near From You starts below */
  0%,
  34% {
    transform: translateY(120%);
    opacity: 0;
  }

  /* Comes upward */
  42% {
    transform: translateY(0);
    opacity: 1;
  }

  /* Stay visible */
  43%,
  78% {
    transform: translateY(0);
    opacity: 1;
  }

  /* Goes downward while KM + TIME returns */
  88%,
  100% {
    transform: translateY(120%);
    opacity: 0;
  }
}

.near-you-slide-box {
  position: relative;
  width: 170px;
  height: 20px;
  overflow: hidden;
}

.near-you-slide-details,
.near-you-slide-label {
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;

  will-change: transform, opacity;
}

.near-you-slide-details {
  animation: nearYouDetailsSlide 4.8s ease-in-out infinite;
}

.near-you-slide-label {
  animation: nearYouLabelSlide 4.8s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .near-you-slide-details {
    animation: none;
    transform: translateY(0);
    opacity: 1;
  }

  .near-you-slide-label {
    display: none;
  }
}
    `}
  </style>
);

const RestaurantImageWithShimmer = ({ src, alt }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const [imageSrc, setImageSrc] = useState(src || ChaafoLogo);

  useEffect(() => {
    setIsImageLoaded(false);

    setImageSrc(src || ChaafoLogo);
  }, [src]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#f8fafc]">
      {/* ======================================= */}
      {/* RUNNING SHIMMER UNTIL IMAGE LOADS */}
      {/* ======================================= */}

      {!isImageLoaded && (
        <div
          className="
            restaurant-card-shimmer
            absolute
            inset-0
            z-10
            h-full
            w-full
          "
        />
      )}

      {/* ======================================= */}
      {/* IMAGE */}
      {/* ======================================= */}

      <img
        src={imageSrc}
        alt={alt || "Restaurant"}
        loading="lazy"
        onLoad={() => {
          setIsImageLoaded(true);
        }}
        onError={() => {
          if (imageSrc !== ChaafoLogo) {
            setIsImageLoaded(false);
            setImageSrc(ChaafoLogo);
            return;
          }

          setIsImageLoaded(true);
        }}
        className={`
  block
  h-full
  w-full
  object-cover

  transition-opacity
  duration-300

  ${isImageLoaded ? "opacity-100" : "opacity-0"}
`}
      />
    </div>
  );
};

export default function RestaurantNear() {
  const navigate = useNavigate();

  const sliderRef = useRef(null);

  const locationRequestedRef = useRef(false);

  const lastCoordinatesKeyRef = useRef("");

  const autoScrollIntervalRef = useRef(null);

  const autoScrollResumeTimeoutRef = useRef(null);
  const loopNormalizeTimeoutRef = useRef(null);
  const globalContext = useGlobalContext();
  const {
    selectedOrderType,
    selectedPlatform,
    selectedCategoryName,
    selectRestaurant,
  } = globalContext;

  const {
    restaurants: nearbyRestaurants,
    getRestaurants: getRestaurantList,
    restaurantLoading: nearbyRestaurantLoading,
    restaurantError: nearbyRestaurantError,
  } = useRestaurant();

  const [locationLoading, setLocationLoading] = useState(true);

  const [locationError, setLocationError] = useState("");

  const [locationSource, setLocationSource] = useState("");

  const [coordinates, setCoordinates] = useState({
    latitude: null,
    longitude: null,
  });

  const [favourites, setFavourites] = useState([]);

  /*
   * Selected address ki saved location object.
   */
  const storedLocation = useMemo(
    () =>
      getStoredObject(
        "selectedDeliveryCoordinates",
        "selectedAddressCoordinates",
        "selectedDeliveryAddressData",
        "selectedAddressData",
        "deliveryAddressData",
        "currentLocation",
      ),
    [
      globalContext?.selectedDeliveryCoordinates,
      globalContext?.selectedAddressCoordinates,
      globalContext?.selectedDeliveryAddress,
    ],
  );

  /*
   * Context/selected address ko first priority milegi.
   */
  const preferredLatitude = getValidCoordinate(
    globalContext?.selectedDeliveryLocation?.latitude,

    globalContext?.selectedDeliveryCoordinates?.latitude,

    globalContext?.selectedAddressCoordinates?.latitude,

    globalContext?.selectedDeliveryAddress?.latitude,

    globalContext?.selectedLatitude,
    globalContext?.selectedLat,

    globalContext?.customerLatitude,
    globalContext?.latitude,

    storedLocation?.latitude,
    storedLocation?.lat,
    storedLocation?.customerLatitude,

    getStoredValue(
      "selectedLatitude",
      "selectedLat",
      "deliveryLatitude",
      "customerLatitude",
      "latitude",
    ),
  );

  const preferredLongitude = getValidCoordinate(
    globalContext?.selectedDeliveryLocation?.longitude,

    globalContext?.selectedDeliveryCoordinates?.longitude,

    globalContext?.selectedDeliveryCoordinates?.lontitude,

    globalContext?.selectedAddressCoordinates?.longitude,

    globalContext?.selectedAddressCoordinates?.lontitude,

    globalContext?.selectedDeliveryAddress?.longitude,

    globalContext?.selectedDeliveryAddress?.lontitude,

    globalContext?.selectedLongitude,
    globalContext?.selectedLng,

    globalContext?.customerLongitude,
    globalContext?.longitude,

    storedLocation?.longitude,
    storedLocation?.lontitude,
    storedLocation?.lng,
    storedLocation?.customerLongitude,

    getStoredValue(
      "selectedLongitude",
      "selectedLng",
      "deliveryLongitude",
      "customerLongitude",
      "longitude",
      "lontitude",
    ),
  );

  const hasPreferredCoordinates =
    Number.isFinite(preferredLatitude) && Number.isFinite(preferredLongitude);

  /*
   * Restaurant API call.
   */
  const fetchNearbyRestaurants = useCallback(
    async ({ latitude, longitude, force = false, source = "current" }) => {
      const cleanLatitude = Number(latitude);

      const cleanLongitude = Number(longitude);

      if (!Number.isFinite(cleanLatitude) || !Number.isFinite(cleanLongitude)) {
        setLocationLoading(false);

        setLocationError("Valid latitude and longitude are required.");

        return {
          success: false,
        };
      }

      const coordinatesKey = `${cleanLatitude}|${cleanLongitude}`;

      if (!force && lastCoordinatesKeyRef.current === coordinatesKey) {
        return {
          success: true,
          skipped: true,
        };
      }

      lastCoordinatesKeyRef.current = coordinatesKey;

      setLocationLoading(true);
      setLocationError("");

      setCoordinates({
        latitude: cleanLatitude,
        longitude: cleanLongitude,
      });

      setLocationSource(source);

      try {
     return await getRestaurantList({
  categoryIds: [],
  customerLatitude: cleanLatitude,
  customerLongitude: cleanLongitude,
  limit: 12,
  offset: 0,
  range: RESTAURANT_RANGE_KM,
  servingType: "",
  append: false,
  forceRefresh: force,
});

      } catch (error) {
        console.error("Nearby restaurant fetch error:", error);

        return {
          success: false,
        };
      } finally {
        setLocationLoading(false);
      }
    },
    [getRestaurantList],
  );

  const requestCurrentLocation = useCallback(
    (force = false) => {
      if (!navigator.geolocation) {
        setLocationLoading(false);
        setLocationError("Location is not supported by this browser.");
        return;
      }

      setLocationLoading(true);
      setLocationError("");

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const latitude = Number(position.coords.latitude);

          const longitude = Number(position.coords.longitude);

          setCoordinates({
            latitude,
            longitude,
          });

          setLocationSource("current");

          try {
            sessionStorage.setItem("customerLatitude", String(latitude));

            sessionStorage.setItem("customerLongitude", String(longitude));
          } catch (error) {
            console.error("Location storage error:", error);
          }

          const result = await fetchNearbyRestaurants({
            latitude,
            longitude,
            force,
            source: "current",
          });

          setLocationLoading(false);
        },

        (error) => {
          console.error("Current location error:", error);

          setLocationError(getLocationErrorMessage(error));

          setLocationLoading(false);
        },

        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: force ? 0 : 60000,
        },
      );
    },
    [fetchNearbyRestaurants],
  );

useEffect(() => {
  if (locationRequestedRef.current) {
    return;
  }

  locationRequestedRef.current = true;

  if (!navigator.geolocation) {
    setLocationLoading(false);
    setLocationError("Location is not supported by this browser.");
    return;
  }

  setLocationLoading(true);
  setLocationError("");

  navigator.geolocation.getCurrentPosition(
    async (position) => {
      const latitude = Number(position.coords.latitude);
      const longitude = Number(position.coords.longitude);

      console.log("PAGE LOAD CURRENT LOCATION:", {
        latitude,
        longitude,
      });

      setCoordinates({
        latitude,
        longitude,
      });

      setLocationSource("current");

      try {
        sessionStorage.setItem("customerLatitude", String(latitude));

        sessionStorage.setItem("customerLongitude", String(longitude));
      } catch (error) {
        console.error("Location storage error:", error);
      }

      try {
        console.log("CALLING V1 API ON PAGE LOAD:", latitude, longitude);

    const result = await getRestaurantList({
      categoryIds: [],
      customerLatitude: latitude,
      customerLongitude: longitude,
      limit: 12,
      offset: 0,
      range: RESTAURANT_RANGE_KM,
      servingType: "",
      bestOf: "",
      append: false,
      forceRefresh: true,
    });
        console.log("PAGE LOAD V1 API RESULT:", result);
      } catch (error) {
        console.error("PAGE LOAD RESTAURANT API ERROR:", error);

        setLocationError(
          error?.message || "Unable to load nearby restaurants.",
        );
      } finally {
        setLocationLoading(false);
      }
    },

    (error) => {
      console.error("PAGE LOAD LOCATION ERROR:", error);

      setLocationError(getLocationErrorMessage(error));

      setLocationLoading(false);
    },

    {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 60000,
    },
  );
}, [getRestaurantList]);

  const locationBasedRestaurants = useMemo(() => {
    return [...nearbyRestaurants].sort((first, second) => {
      const firstDistance = Number(first?.distance);
      const secondDistance = Number(second?.distance);

      if (Number.isFinite(firstDistance) && Number.isFinite(secondDistance)) {
        return firstDistance - secondDistance;
      }

      if (Number.isFinite(firstDistance)) {
        return -1;
      }

      if (Number.isFinite(secondDistance)) {
        return 1;
      }

      return 0;
    });
  }, [nearbyRestaurants]);

  const loopedRestaurants = useMemo(() => {
    if (locationBasedRestaurants.length <= 1) {
      return locationBasedRestaurants;
    }

    return [
      ...locationBasedRestaurants,
      ...locationBasedRestaurants,
      ...locationBasedRestaurants,
    ];
  }, [locationBasedRestaurants]);

  const getSliderScrollMetrics = useCallback(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return {
        cardStep: 324,
        visibleCount: 1,
        scrollAmount: 324,
        cycleWidth: 324,
      };
    }

    const firstCard = slider.firstElementChild;

    if (!firstCard) {
      return {
        cardStep: 324,
        visibleCount: 1,
        scrollAmount: 324,
        cycleWidth: 324,
      };
    }

    // Actual card width
    const cardWidth = firstCard.getBoundingClientRect().width;

    // Actual flex gap
    const sliderStyles = window.getComputedStyle(slider);

    const gap =
      Number.parseFloat(sliderStyles.columnGap || sliderStyles.gap || "0") || 0;

    // One complete card including gap
    const cardStep = cardWidth + gap;

    /*
     * Calculate how many COMPLETE cards
     * are currently visible.
     *
     * Example:
     * 1290px container => 4
     * 970px container  => 3
     * 650px container  => 2
     * mobile           => 1
     */
    const visibleCount = Math.max(
      1,
      Math.floor((slider.clientWidth + gap) / cardStep),
    );

    // Move complete visible group
    const scrollAmount = cardStep * visibleCount;

    /*
     * Your JSX duplicates restaurants:
     *
     * [...restaurants, ...restaurants]
     *
     * Therefore one original cycle is:
     * number of restaurants × one card step.
     */
    const cycleWidth = locationBasedRestaurants.length * cardStep;

    return {
      cardStep,
      visibleCount,
      scrollAmount,
      cycleWidth,
    };
  }, [locationBasedRestaurants.length]);

  useLayoutEffect(() => {
    const slider = sliderRef.current;

    if (!slider || locationBasedRestaurants.length <= 1) {
      return;
    }

    const { cycleWidth } = getSliderScrollMetrics();

    if (!Number.isFinite(cycleWidth) || cycleWidth <= 0) {
      return;
    }

    // Start from the second/middle copy.
    // Visually user still sees restaurant #1 first.
    slider.scrollTo({
      left: cycleWidth,
      behavior: "auto",
    });
  }, [locationBasedRestaurants.length, getSliderScrollMetrics]);

  const moveSlider = useCallback(
    (direction = "right") => {
      const slider = sliderRef.current;

      if (!slider || locationBasedRestaurants.length <= 1) {
        return;
      }

      const { visibleCount, scrollAmount, cycleWidth } =
        getSliderScrollMetrics();

      if (
        !Number.isFinite(scrollAmount) ||
        scrollAmount <= 0 ||
        !Number.isFinite(cycleWidth) ||
        cycleWidth <= 0
      ) {
        return;
      }

      // Previous pending reset cancel
      if (loopNormalizeTimeoutRef.current) {
        window.clearTimeout(loopNormalizeTimeoutRef.current);

        loopNormalizeTimeoutRef.current = null;
      }

      let currentScroll = slider.scrollLeft;

      // =================================================
      // RIGHT
      // =================================================

      if (direction === "right") {
        const targetScroll = currentScroll + scrollAmount;

        // User ko sirf forward movement dikhega
        slider.scrollTo({
          left: targetScroll,
          behavior: "smooth",
        });

        /*
         * Third copy me enter hone ke baad
         * same visual position ko middle copy
         * me INSTANTLY shift karenge.
         *
         * No backward animation.
         */
        loopNormalizeTimeoutRef.current = window.setTimeout(() => {
          const currentSlider = sliderRef.current;

          if (!currentSlider) {
            return;
          }

          const position = currentSlider.scrollLeft;

          if (position >= cycleWidth * 2) {
            // IMPORTANT:
            // Direct assignment = instant reset.
            // User ko kuch backward nahi dikhega.
            currentSlider.scrollLeft = position - cycleWidth;
          }
        }, 450);

        return;
      }

      // =================================================
      // LEFT
      // =================================================

      /*
       * Agar middle copy ke beginning par hain,
       * pehle same position third side par silently
       * set karo.
       */
      if (currentScroll <= cycleWidth + 5) {
        slider.scrollLeft = currentScroll + cycleWidth;

        currentScroll = slider.scrollLeft;
      }

      const targetScroll = currentScroll - scrollAmount;

      slider.scrollTo({
        left: targetScroll,
        behavior: "smooth",
      });

      /*
       * Left side normalization bhi instant hai.
       */
      loopNormalizeTimeoutRef.current = window.setTimeout(() => {
        const currentSlider = sliderRef.current;

        if (!currentSlider) {
          return;
        }

        const position = currentSlider.scrollLeft;

        if (position < cycleWidth) {
          currentSlider.scrollLeft = position + cycleWidth;
        }
      }, 450);
    },
    [getSliderScrollMetrics, locationBasedRestaurants.length],
  );

  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      window.clearInterval(autoScrollIntervalRef.current);

      autoScrollIntervalRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    stopAutoScroll();

    if (locationBasedRestaurants.length <= 1) {
      return;
    }

    autoScrollIntervalRef.current = window.setInterval(() => {
      moveSlider("right");
    }, AUTO_SCROLL_DELAY);
  }, [moveSlider, locationBasedRestaurants.length, stopAutoScroll]);

  const restartAutoScroll = useCallback(() => {
    stopAutoScroll();

    if (autoScrollResumeTimeoutRef.current) {
      window.clearTimeout(autoScrollResumeTimeoutRef.current);
    }

    autoScrollResumeTimeoutRef.current = window.setTimeout(() => {
      startAutoScroll();
    }, AUTO_SCROLL_RESUME_DELAY);
  }, [startAutoScroll, stopAutoScroll]);

  const scrollSlider = (direction) => {
    moveSlider(direction);
    restartAutoScroll();
  };

  const handleRetry = () => {
    if (!hasPreferredCoordinates) {
      setLocationError("Please select a delivery location.");

      return;
    }

    lastCoordinatesKeyRef.current = "";

    fetchNearbyRestaurants({
      latitude: preferredLatitude,
      longitude: preferredLongitude,
      force: true,
      source: "selected",
    });
  };

  const toggleFavourite = (event, restaurantCode) => {
    event.stopPropagation();

    setFavourites((previous) => {
      if (previous.includes(restaurantCode)) {
        return previous.filter((code) => code !== restaurantCode);
      }

      return [...previous, restaurantCode];
    });
  };

  const getRestaurantServingType = (restaurant) => {
    const rawRestaurant = restaurant?.raw || restaurant;

    return String(
      restaurant?.servingType ||
        restaurant?.serving_type ||
        rawRestaurant?.servingType ||
        rawRestaurant?.serving_type ||
        "",
    )
      .trim()
      .toUpperCase();
  };

  const FoodTypeIcon = ({ type }) => {
    const isVeg = type === "V";
    const isNonVeg = type === "N";

    if (!isVeg && !isNonVeg) {
      return null;
    }

    const color = isVeg ? "#16a34a" : "#b91c1c";

    return (
      <div
        title={isVeg ? "Pure Veg" : "Non Veg"}
        className="
        flex
        h-5
        w-5
        items-center
        justify-center
        border
        bg-white
        shadow-sm
      "
        style={{
          borderColor: color,
          borderRadius: "2px",
        }}
      >
        <div
          className="
          flex
          h-[14px]
          w-[14px]
          items-center
          justify-center
          border
          border-gray-300
          bg-[#f8f8f8]
        "
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    );
  };

 const handleRestaurantClick = (restaurant) => {
   const restaurantUserId = getRestaurantUserId(restaurant);

   if (!restaurantUserId) {
     console.error("Restaurant userid missing:", restaurant);

     return;
   }

   const currentOrderType = String(selectedOrderType || "home_delivery").trim();

   const currentPlatform = String(selectedPlatform || "").trim();

   const selectedItemName = String(selectedCategoryName || "").trim();

  const selectedRestaurantData = {
    userId: restaurantUserId,

    name: restaurant?.name || restaurant?.restaurantName || "Restaurant",

    image: restaurant?.image || restaurant?.restaurantImage || "",

    address: String(restaurant?.address || restaurant?.street || "").trim(),

    phone: String(
      restaurant?.phone || restaurant?.restaurant_phone || "",
    ).trim(),

    city: String(restaurant?.city || "").trim(),

    state: String(restaurant?.state || "").trim(),

    latitude: restaurant?.latitude ?? "",

    longitude: restaurant?.longitude ?? restaurant?.lontitude ?? "",

    servingType: getRestaurantServingType(restaurant),

    startingPrice: Number.isFinite(
      Number(restaurant?.startingPrice ?? restaurant?.starting_price),
    )
      ? Number(restaurant?.startingPrice ?? restaurant?.starting_price)
      : null,

    customerLatitude: coordinates.latitude,
    customerLongitude: coordinates.longitude,

    selectedItemName,
  };

   // ============================================
   // SAVE SELECTED RESTAURANT
   // ============================================

   selectRestaurant(selectedRestaurantData);

   // ============================================
   // OPEN RESTAURANT DETAILS
   // ============================================

   navigate(`/restaurant-details/${restaurantUserId}`, {
     state: {
       restaurant: selectedRestaurantData,

       orderType: currentOrderType,

       platform: currentPlatform,

       selectedItemName,
     },
   });
 };

 const handleRestaurantKeyDown = (event, restaurant) => {
   if (event.key !== "Enter" && event.key !== " ") {
     return;
   }

   event.preventDefault();

   handleRestaurantClick(restaurant);
 };

  const showLoading = locationLoading || nearbyRestaurantLoading;

  const visibleError = locationError || nearbyRestaurantError;

  useEffect(() => {
    if (showLoading || visibleError || locationBasedRestaurants.length <= 1) {
      stopAutoScroll();

      return undefined;
    }

    startAutoScroll();

    return () => {
      stopAutoScroll();

      if (autoScrollResumeTimeoutRef.current) {
        window.clearTimeout(autoScrollResumeTimeoutRef.current);
      }
    };
  }, [
    showLoading,
    visibleError,
    locationBasedRestaurants.length,
    startAutoScroll,
    stopAutoScroll,
  ]);

  return (
    <>
      <ShimmerStyles />
      <section className="w-full bg-blue-50 pt-5 pb-3">
        <div className="mx-auto w-full max-w-[1350px] px-4 sm:px-5 md:px-8">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-2xl font-semibold leading-tight tracking-tight text-[#1f2937] sm:text-3xl lg:text-4xl">
                Restaurants near you
              </h2>

              <div className="mt-2 flex items-center gap-2 text-xs text-[#91a2ba] sm:text-sm md:text-base">
                {showLoading ? (
                  <>
                    <Loader2 size={15} className="shrink-0 animate-spin" />

                    <span>Finding restaurants near your location...</span>
                  </>
                ) : coordinates.latitude !== null &&
                  coordinates.longitude !== null ? (
                  <>
                    <MapPin size={15} className="shrink-0" />

                    <span>
                      {locationSource === "selected"
                        ? "Based on your selected delivery location"
                        : "Based on your current location"}
                    </span>
                  </>
                ) : (
                  <>
                    <MapPin size={15} className="shrink-0" />

                    <span>Location access is required</span>
                  </>
                )}
              </div>
            </div>

            {!showLoading &&
              !visibleError &&
              locationBasedRestaurants.length > 0 && (
                <div className="hidden w-full justify-end gap-2 sm:flex sm:w-auto sm:justify-start">
                  <button
                    type="button"
                    onClick={() => scrollSlider("left")}
                    aria-label="Previous restaurants"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e8ef] bg-white text-[#94a6bd] shadow-sm transition hover:border-[#b9c3d0] hover:text-[#111827] active:scale-95 sm:h-11 sm:w-11"
                  >
                    <ArrowLeft
                      size={20}
                      strokeWidth={2}
                      className="sm:h-6 sm:w-6"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => scrollSlider("right")}
                    aria-label="Next restaurants"
                    className="relative flex h-10 w-10 items-center justify-center rounded-full border border-[#e3e8ef] bg-white text-[#94a6bd] shadow-sm transition hover:border-[#b9c3d0] hover:text-[#111827] active:scale-95 sm:h-11 sm:w-11"
                  >
                    <ArrowRight
                      size={20}
                      strokeWidth={2}
                      className="sm:h-6 sm:w-6"
                    />
                  </button>
                </div>
              )}
          </div>

          {/* Loading skeleton */}
          {showLoading && (
            <div className="flex gap-4 overflow-hidden pb-3 sm:gap-6">
              {Array.from({
                length: 4,
              }).map((_, index) => (
                <div
                  key={`nearby-skeleton-${index}`}
                  className="w-[82vw] max-w-[300px] shrink-0 overflow-hidden rounded-[17px] border border-[#e5e9ee] bg-white sm:w-[300px]"
                >
                  <div className="h-[185px] animate-pulse bg-[#e9edf2] sm:h-[206px]" />

                  <div className="p-3">
                    <div className="h-5 w-[85%] animate-pulse rounded bg-[#e9edf2]" />

                    <div className="mt-2 h-3.5 w-[55%] animate-pulse rounded bg-[#edf0f4]" />

                    <div className="mt-3 flex gap-2">
                      <div className="h-6 w-14 animate-pulse rounded-full bg-[#e9edf2]" />

                      <div className="h-6 w-24 animate-pulse rounded-full bg-[#edf0f4]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {!showLoading && visibleError && (
            <div className="flex min-h-[210px] flex-col items-center justify-center rounded-[20px] border border-dashed border-[#d7dde5] bg-white px-5 text-center">
              <MapPin size={28} className="text-[#ff6534]" />

              <p className="mt-4 text-[15px] font-semibold text-[#252a32]">
                Nearby restaurants could not be loaded
              </p>

              <p className="mt-1 max-w-md text-[13px] leading-5 text-[#8491a4]">
                {visibleError}
              </p>

              <button
                type="button"
                onClick={handleRetry}
                className="mt-5 flex h-11 items-center justify-center gap-2 rounded-xl bg-[#111827] px-5 text-sm font-semibold text-white"
              >
                <RefreshCw size={17} />
                Try again
              </button>
            </div>
          )}

          {/* Empty */}
          {!showLoading &&
            !visibleError &&
            locationBasedRestaurants.length === 0 && (
              <div className="flex min-h-[210px] flex-col items-center justify-center rounded-[20px] border border-dashed border-[#d7dde5] bg-white px-5 text-center">
                <MapPin size={30} className="text-[#91a2ba]" />

                <p className="mt-4 text-[15px] font-semibold text-[#252a32]">
                  No nearby restaurants found
                </p>

                <p className="mt-1 text-[13px] text-[#8491a4]">
                  No restaurants are available within {RESTAURANT_RANGE_KM} km.
                </p>
              </div>
            )}

          {/* Restaurant slider */}
          {!showLoading &&
            !visibleError &&
            locationBasedRestaurants.length > 0 && (
              <div
                ref={sliderRef}
                onMouseEnter={stopAutoScroll}
                onMouseLeave={restartAutoScroll}
                onTouchStart={stopAutoScroll}
                onTouchEnd={restartAutoScroll}
                className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 pt-1 sm:gap-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {loopedRestaurants.map((restaurant, index) => {
                  const restaurantCode =
                    restaurant.shopCode || `nearby-${index}`;

                  const restaurantName =
                    restaurant.name ||
                    restaurant.restaurant_name ||
                    restaurant.shopName ||
                    "Restaurant";

                  const servingType = getRestaurantServingType(restaurant);

                  const isFavourite = favourites.includes(restaurantCode);

                  const rawRestaurant = restaurant?.raw || restaurant;

                  const distanceValue = Number(
                    restaurant?.distance ?? rawRestaurant?.distance,
                  );

                  const estimatedTimeValue = Number(
                    restaurant?.estimatedTime ??
                      restaurant?.estimated_time ??
                      restaurant?.deliveryTime ??
                      rawRestaurant?.estimated_time ??
                      rawRestaurant?.estimatedTime ??
                      rawRestaurant?.delivery_time ??
                      rawRestaurant?.deliveryTime,
                  );

                  const hasDistance =
                    Number.isFinite(distanceValue) && distanceValue >= 0;

                  const hasEstimatedTime =
                    Number.isFinite(estimatedTimeValue) &&
                    estimatedTimeValue > 0;

                  const isNearYou =
                    hasEstimatedTime && estimatedTimeValue <= 30;

                  const distanceText = hasDistance
                    ? `${distanceValue.toFixed(1)} km`
                    : "";

                  const estimatedTimeText = hasEstimatedTime
                    ? `${Math.round(estimatedTimeValue)}-${Math.round(estimatedTimeValue) + 10} min`
                    : "";

                  const restaurantImages = Array.isArray(restaurant.image)
                    ? restaurant.image
                    : String(restaurant.image || "")
                        .split(",")
                        .map((url) => url.trim())
                        .filter(Boolean);

                  const firstImage = restaurantImages[0] || ChaafoLogo;

                  return (
                    <article
                      key={`${restaurantCode}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRestaurantClick(restaurant)}
                      onKeyDown={(event) =>
                        handleRestaurantKeyDown(event, restaurant)
                      }
                      className="
  group

  w-[82vw]
  min-w-[260px]
  max-w-[300px]

  sm:w-[calc((100%-1.5rem)/2)]
  sm:max-w-none

  lg:w-[calc((100%-4.5rem)/4)]
  lg:min-w-0
  lg:max-w-none

  shrink-0
  snap-start

  cursor-pointer
  overflow-hidden
  rounded-[17px]
  border
  border-[#dfe4ea]
  bg-white
  outline-none

  transition-[border-color,box-shadow,transform]
  duration-300

  hover:-translate-y-0.5
  hover:shadow-[0_5px_16px_rgba(15,23,42,0.08)]

  focus-visible:ring-2
  focus-visible:ring-[#2B4578]
"
                    >
                      {/* Image */}
                      <div className="relative aspect-[16/11] w-full overflow-hidden rounded-t-[16px] bg-[#f8fafc]">
                        <RestaurantImageWithShimmer
                          src={firstImage}
                          alt={restaurantName}
                        />

                        {/* VEG / NON VEG ICON */}
                        {(servingType === "V" || servingType === "N") && (
                          <div className="absolute left-3 top-3 z-20">
                            <FoodTypeIcon type={servingType} />
                          </div>
                        )}

                        {/* RATING */}
                        {Number(restaurant.rating) > 0 && (
                          <div
                            className="
        absolute
        right-2
        top-3
        z-20
        flex
        items-center
        gap-1
        rounded-full
        bg-[#16a34a]
        px-2
        py-1
        text-[10px]
        font-semibold
        text-white
        shadow-md
      "
                          >
                            <Star
                              size={13}
                              strokeWidth={0}
                              fill="currentColor"
                            />

                            <span>{Number(restaurant.rating).toFixed(1)}</span>
                          </div>
                        )}
                      </div>

                      {/* Information */}
                      <div className="relative z-10 rounded-b-[16px] bg-white px-[12px] pb-[13px] pt-[11px]">
                        <h3
                          title={restaurantName}
                          className="truncate text-[17px] font-medium leading-6 text-[#505050]"
                        >
                          {restaurantName}
                        </h3>

                        <div className="mt-2.5 flex min-h-[18px] items-center justify-start">
                          {isNearYou ? (
                            <div className="near-you-slide-box">
                              {/* ================================================= */}
                              {/* FIRST - DISTANCE + TIME */}
                              {/* ================================================= */}

                              <div className="near-you-slide-details gap-3 text-[12px] font-medium text-[#718096]">
                                {hasDistance && (
                                  <div className="flex shrink-0 items-center gap-1">
                                    <MapPin
                                      size={14}
                                      strokeWidth={1.8}
                                      className="shrink-0"
                                    />

                                    <span className="whitespace-nowrap">
                                      {distanceText}
                                    </span>
                                  </div>
                                )}

                                {hasEstimatedTime && (
                                  <div className="flex shrink-0 items-center gap-1">
                                    <Clock3
                                      size={14}
                                      strokeWidth={1.8}
                                      className="shrink-0"
                                    />

                                    <span className="whitespace-nowrap">
                                      {estimatedTimeText}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* ================================================= */}
                              {/* SECOND - NEAR FROM YOU */}
                              {/* ================================================= */}

                              <div className="near-you-slide-label gap-1.5 text-[12px] font-semibold text-[#16a34a]">
                                <MapPin
                                  size={14}
                                  strokeWidth={2}
                                  className="shrink-0"
                                />

                                <span className="whitespace-nowrap">
                                  Near From You
                                </span>
                              </div>
                            </div>
                          ) : (
                            // =====================================================
                            // > 30 MIN
                            // NORMAL DISTANCE + TIME
                            // =====================================================

                            <div className="flex items-center justify-start gap-3">
                              {hasDistance && (
                                <div className="flex shrink-0 items-center gap-1 text-[12px] font-medium text-[#718096]">
                                  <MapPin
                                    size={14}
                                    strokeWidth={1.8}
                                    className="shrink-0"
                                  />

                                  <span className="whitespace-nowrap">
                                    {distanceText}
                                  </span>
                                </div>
                              )}

                              {hasEstimatedTime && (
                                <div className="flex shrink-0 items-center gap-1 text-[12px] font-medium text-[#718096]">
                                  <Clock3
                                    size={14}
                                    strokeWidth={1.8}
                                    className="shrink-0"
                                  />

                                  <span className="whitespace-nowrap">
                                    {estimatedTimeText}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
        </div>
      </section>
    </>
  );
}
